import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import MainLayout from '../layout/MainLayout';
import planApi from '../../services/planApi';
import config from '../../config';
import { Check, Mail, Phone, Target, Star, Calendar, ChevronLeft } from 'lucide-react';
import './admin-pdi-progress.scss';

interface Person { name: string; image: string | null; role: string; email: string; phone: string; }
interface Comp { title: string; progress: number; }
interface MonthCell { idx: number; ym: string; label: string; status: 'done' | 'missed' | 'upcoming'; }
interface MonthRating {
  idx: number; label: string;
  mentee_rating: number | null; mentee_comment: string | null;
  mentor_rating: number | null; mentor_comment: string | null;
}
interface Detail {
  relationship_id: number;
  mentee: Person; mentor: Person;
  progress: number;
  foda_done: boolean; pdi_done: boolean;
  months: MonthCell[];
  competencies: Comp[];
  monthly_ratings: MonthRating[];
}
interface DuplaRow {
  relationship_id: number;
  mentee: string; mentor: string;
  mentee_image: string | null; mentor_image: string | null;
  competencies: number; progress: number;
}

const getImageUrl = (imagePath?: string | null): string | null => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  return `${config.api.baseUrl.replace(/\/api\/?$/, '')}/${imagePath}`;
};
const initials = (name: string) =>
  name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

const eyebrowDate = () => {
  const d = new Date();
  const dia = d.toLocaleDateString('es', { weekday: 'long' });
  const mes = d.toLocaleDateString('es', { month: 'short' }).replace('.', '');
  return `${dia} ${d.getDate()} de ${mes} · Administrador`.toUpperCase();
};

const Stars: React.FC<{ n: number | null }> = ({ n }) => (
  <span className="pp-stars">
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} size={15} className={n && i <= n ? 'on' : 'off'} fill={n && i <= n ? 'currentColor' : 'none'} />
    ))}
  </span>
);

const Avatar: React.FC<{ name: string; image: string | null; size?: number }> = ({ name, image, size = 56 }) => {
  const img = getImageUrl(image);
  return (
    <span className="pp-avatar" style={{ width: size, height: size }}>
      {img ? <img src={img} alt={name} /> : <span className="pp-avatar__ini">{initials(name)}</span>}
    </span>
  );
};

const PersonCard: React.FC<{ p: Person; role: 'MENTEE' | 'MENTOR' }> = ({ p, role }) => (
  <div className="pp-person">
    <Avatar name={p.name} image={p.image} />
    <div className="pp-person__info">
      <div className="pp-person__name">{p.name}</div>
      <div className="pp-person__role">{role}{p.role ? ` · ${p.role}` : ''}</div>
      {p.email && <div className="pp-person__line"><Mail size={13} /> {p.email}</div>}
      {p.phone && <div className="pp-person__line"><Phone size={13} /> {p.phone}</div>}
    </div>
  </div>
);

const AdminPdiProgress: React.FC = () => {
  const [list, setList] = useState<DuplaRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<number | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    planApi.getPdiProgress()
      .then((res: { duplas: DuplaRow[] }) => setList(res.duplas))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, []);

  const openDupla = (relId: number) => {
    setSelected(relId);
    setDetail(null);
    setLoadingDetail(true);
    planApi.getPdiDetail(relId)
      .then((res: Detail) => setDetail(res))
      .catch(() => setDetail(null))
      .finally(() => setLoadingDetail(false));
  };

  // Deep-link: /admin/pdi-progress?rel=ID abre directamente el detalle de esa dupla.
  useEffect(() => {
    const rel = searchParams.get('rel');
    if (rel && !isNaN(Number(rel))) openDupla(Number(rel));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const back = () => { setSelected(null); setDetail(null); };

  return (
    <MainLayout>
      <div className="admin-pdi-progress">
        <div className="page-header">
          <div className="page-header__info">
            <div className="eyebrow">{eyebrowDate()}</div>
            <h1 className="heading-1">Avance del <em>PDI</em></h1>
            <p className="pp-intro">Progreso por pareja. Despliega una dupla para ver competencias y compromisos.</p>
          </div>
        </div>

        {loading ? (
          <div className="pp-loading">Cargando avance…</div>
        ) : selected === null ? (
          /* ---------- LISTA DE DUPLAS ---------- */
          <div className="pp-card">
            {(list || []).length === 0 ? (
              <p className="pp-empty">No hay duplas activas.</p>
            ) : (list || []).map(d => (
              <button className="pp-row" key={d.relationship_id} onClick={() => openDupla(d.relationship_id)}>
                <div className="pp-row__avatars">
                  <Avatar name={d.mentor} image={d.mentor_image} size={38} />
                  <Avatar name={d.mentee} image={d.mentee_image} size={38} />
                </div>
                <div className="pp-row__names">
                  <span className="pp-row__mentee">{d.mentee}</span>
                  <span className="pp-row__mentor">↪ {d.mentor}</span>
                </div>
                <div className="pp-row__bar-wrap">
                  <span className="pp-row__bar"><span className="pp-row__fill" style={{ width: `${d.progress}%` }} /></span>
                  <span className="pp-row__pct">{d.progress}%</span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          /* ---------- DETALLE DE UNA DUPLA ---------- */
          <div className="pp-detail">
            <button className="pp-back" onClick={back}><ChevronLeft size={16} /> Volver a duplas</button>

            {loadingDetail || !detail ? (
              <div className="pp-loading">Cargando detalle…</div>
            ) : (
              <>
                {/* Cabecera: perfiles + progreso total */}
                <div className="pp-card pp-head">
                  <div className="pp-head__people">
                    <PersonCard p={detail.mentee} role="MENTEE" />
                    <PersonCard p={detail.mentor} role="MENTOR" />
                  </div>
                  <div className="pp-head__progress">
                    <div className="pp-head__pct">{detail.progress}%</div>
                    <span className="pp-head__bar"><span className="pp-head__fill" style={{ width: `${detail.progress}%` }} /></span>
                    <div className="pp-head__lbl">Progreso total PDI</div>
                  </div>
                </div>

                {/* Hitos */}
                <div className="pp-card pp-hitos">
                  <div className="pp-section-eyebrow">Hitos</div>
                  <div className="pp-hitos__pills">
                    <span className={`pp-hito ${detail.foda_done ? 'is-done' : 'is-pending'}`}>
                      <Check size={14} /> FODA {detail.foda_done ? 'terminado' : 'pendiente'}
                    </span>
                    <span className={`pp-hito ${detail.pdi_done ? 'is-done' : 'is-pending'}`}>
                      <Check size={14} /> PDI {detail.pdi_done ? 'terminado' : 'pendiente'}
                    </span>
                  </div>
                </div>

                {/* Agendamiento de sesiones */}
                <div className="pp-card">
                  <h3 className="pp-title"><Calendar size={18} /> Agendamiento de sesiones</h3>
                  <div className="pp-months">
                    {detail.months.map(m => (
                      <div key={m.idx} className={`pp-cell pp-cell--${m.status}`} title={m.label}>
                        <span className="pp-cell__mark">
                          {m.status === 'done' ? <Check size={15} /> : m.status === 'missed' ? '—' : '·'}
                        </span>
                        <span className="pp-cell__lbl">M{m.idx}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Objetivos del PDI */}
                <div className="pp-card">
                  <h3 className="pp-title"><Target size={18} /> Objetivos del PDI</h3>
                  {detail.competencies.length === 0 ? (
                    <p className="pp-empty pp-empty--flat">Aún no hay objetivos en el PDI.</p>
                  ) : (
                    <div className="pp-objs">
                      {detail.competencies.map((c, i) => (
                        <div className="pp-obj" key={i}>
                          <div className="pp-obj__head">
                            <span className="pp-obj__title">{c.title}</span>
                            <span className="pp-obj__pct">{c.progress}%</span>
                          </div>
                          <span className="pp-obj__bar"><span className="pp-obj__fill" style={{ width: `${c.progress}%` }} /></span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Calificación por mes */}
                <div className="pp-card">
                  <h3 className="pp-title"><Star size={18} /> Calificación por mes</h3>
                  {detail.monthly_ratings.length === 0 ? (
                    <p className="pp-empty pp-empty--flat">Aún no hay calificaciones registradas.</p>
                  ) : (
                    <div className="pp-ratings">
                      {detail.monthly_ratings.map(r => (
                        <div className="pp-rating" key={r.idx}>
                          <div className="pp-rating__month">Mes {r.idx}</div>
                          <div className="pp-rating__rows">
                            <div className="pp-rating__row">
                              <span className="pp-tag pp-tag--mentee">MENTEE</span>
                              <Stars n={r.mentee_rating} />
                              {r.mentee_comment
                                ? <span className="pp-rating__cmt">“{r.mentee_comment}”</span>
                                : <span className="pp-rating__cmt pp-rating__cmt--muted">{r.mentee_rating ? 'sin comentario' : 'sin calificación'}</span>}
                            </div>
                            <div className="pp-rating__row">
                              <span className="pp-tag pp-tag--mentor">MENTOR</span>
                              <Stars n={r.mentor_rating} />
                              {r.mentor_comment
                                ? <span className="pp-rating__cmt">“{r.mentor_comment}”</span>
                                : <span className="pp-rating__cmt pp-rating__cmt--muted">{r.mentor_rating ? 'sin comentario' : 'sin calificación'}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AdminPdiProgress;
